import { toPositiveOptionId } from '@/utils/optionValues';
import { buildFirstInstallmentOptions as buildSharedFirstInstallmentOptions } from '@/composables/shared/useFirstInstallmentOptions';
import {
  EXPENSE_FEE_STATUS_OPTIONS,
  EXPENSE_FEE_FREQUENCY_OPTIONS,
  EXPENSE_FEE_TAX_MODE_OPTIONS,
  EXPENSE_FEE_LOCAL_TAX_TYPE_OPTIONS,
  parseExpenseFeeNumericInput,
  formatExpenseFeeDateForApi,
  buildTodayDate,
  createInitialExpenseFeeTaxPreview,
  normalizeExpenseFeeLocalTaxes,
  buildExpenseFeeTagsPayload,
  buildExpenseFeeTaxPreviewPayload,
  buildExpenseFeeStoreRequestData,
} from '@/composables/expensesFees/shared/useExpenseFeeModalFormShared';
import { EXPENSES_TEXT } from '@/texts/catalog/expenses';

export const EXPENSE_MODAL_SECTIONS = [
  {
    key: 'general',
    icon: 'fa-file-text-o',
  },
  {
    key: 'operational',
    icon: 'fa-cogs',
  },
  {
    key: 'details',
    icon: 'fa-align-left',
  },
  {
    key: 'files',
    icon: 'fa-paperclip',
  },
];

export const EXPENSE_DOCUMENT_TYPE_OPTIONS = [
  { value: 'cfdi', label: EXPENSES_TEXT.modal.selectOptions.documentTypes.cfdi },
  { value: 'simple_note', label: EXPENSES_TEXT.modal.selectOptions.documentTypes.simpleNote },
  { value: 'foreign_receipt', label: EXPENSES_TEXT.modal.selectOptions.documentTypes.foreignReceipt },
  { value: 'payroll', label: EXPENSES_TEXT.modal.selectOptions.documentTypes.payroll },
];

export const EXPENSE_LOCAL_TAX_TYPE_OPTIONS = [
  ...EXPENSE_FEE_LOCAL_TAX_TYPE_OPTIONS,
];

export { buildSharedFirstInstallmentOptions as buildFirstInstallmentOptions };

export const createInitialExpenseForm = () => ({
  voucherType: 'cfdi',
  expenseStatus: 'pending',
  isApproved: false,
  currencyCode: 'MXN',
  exchangeRate: 1,
  amount: 0,
  discount: 0,
  deductions: 0,
  withoutTaxes: false,
  taxType: 'individual',
  taxes: [],
  taxGroup: null,
  localTaxes: [],
  supplier: null,
  supplierContributor: null,
  expenseClass: null,
  expenseCategory: null,
  frequency: 'unique',
  expensesCount: 1,
  firstInstallmentOption: null,
  periodicityOption: null,
  folio: '',
  concept: '',
  expenseDate: buildTodayDate(),
  paymentDate: buildTodayDate(),
  cfdiDate: buildTodayDate(),
  customer: null,
  sale: null,
  invoicePdfFile: null,
  invoiceXmlFile: null,
  voucherFile: null,
  purchaseOrderFile: null,
  additionalFile: null,
  additionalFileMode: 'file',
  additionalFileUrl: '',
  tags: [],
  area: null,
  comments: '',
});

export const createInitialExpenseTaxPreview = () => ({
  ...createInitialExpenseFeeTaxPreview(),
});

export const createInitialExpenseModalOptions = () => ({
  documentTypes: EXPENSE_DOCUMENT_TYPE_OPTIONS,
  expenseStatuses: EXPENSE_FEE_STATUS_OPTIONS,
  frequencies: EXPENSE_FEE_FREQUENCY_OPTIONS,
  taxModes: EXPENSE_FEE_TAX_MODE_OPTIONS,
  localTaxTypes: EXPENSE_LOCAL_TAX_TYPE_OPTIONS,
  currencies: [],
  suppliers: [],
  supplierContributors: [],
  customers: [],
  sales: [],
  taxes: [],
  taxGroups: [],
  periodicities: [],
  tags: [],
  areas: [],
});

export const buildExpenseTaxPreviewPayload = (form = {}) => {
  return buildExpenseFeeTaxPreviewPayload(form);
};

export const buildExpensePayload = (form = {}, taxPreview = null) => {
  const payloadPreview = taxPreview && typeof taxPreview === 'object'
    ? taxPreview
    : createInitialExpenseTaxPreview();

  const { tagIds, tagNames } = buildExpenseFeeTagsPayload(form?.tags);
  const discount = parseExpenseFeeNumericInput(form?.discount);
  const deductions = parseExpenseFeeNumericInput(form?.deductions);
  const withoutTaxes = Boolean(form?.withoutTaxes);
  const taxType = String(form?.taxType || 'individual').trim().toLowerCase();

  return {
    voucher_type: String(form?.voucherType || 'cfdi').trim().toLowerCase(),
    expense_status: 'pending',
    is_approved: Boolean(form?.isApproved) ? 1 : 0,
    approved: Boolean(form?.isApproved),
    currency: String(form?.currencyCode || 'MXN').trim().toUpperCase(),
    exchange_rate: Number(form?.exchangeRate || 0) > 0 ? Number(form.exchangeRate) : 1,
    amount: parseExpenseFeeNumericInput(form?.amount),
    // Keep legacy discount behavior by sending combined discount amount.
    discount: discount + deductions,
    deductions,
    tax_type: withoutTaxes ? 'individual' : taxType,
    tax_ids: withoutTaxes
      ? []
      : (Array.isArray(form?.taxes) ? form.taxes : [])
        .map((item) => toPositiveOptionId(item))
        .filter((id) => id > 0),
    tax_group_id: withoutTaxes ? 0 : toPositiveOptionId(form?.taxGroup),
    local_taxes: normalizeExpenseFeeLocalTaxes(form?.localTaxes),
    supplier_id: toPositiveOptionId(form?.supplier),
    class_id: toPositiveOptionId(form?.expenseClass),
    category_id: toPositiveOptionId(form?.expenseCategory),
    frequency: String(form?.frequency || 'unique').trim().toLowerCase(),
    expenses_count: Math.max(1, Number(form?.expensesCount || 1) || 1),
    first_installment: String(form?.firstInstallmentOption?.value || '').trim(),
    periodicity_code: String(form?.periodicityOption?.value || '').trim().toLowerCase(),
    folio: String(form?.folio || '').trim(),
    concept: String(form?.concept || '').trim(),
    date: formatExpenseFeeDateForApi(form?.expenseDate),
    payment_date: formatExpenseFeeDateForApi(form?.paymentDate),
    cfdi_date: formatExpenseFeeDateForApi(form?.cfdiDate),
    customer_id: toPositiveOptionId(form?.customer),
    sale_id: toPositiveOptionId(form?.sale),
    area_id: toPositiveOptionId(form?.area),
    tag_ids: tagIds,
    tags: tagNames,
    comments: String(form?.comments || '').trim(),
    invoice_pdf_file: form?.invoicePdfFile || null,
    invoice_xml_file: form?.invoiceXmlFile || null,
    voucher_file: form?.voucherFile || null,
    purchase_order_file: form?.purchaseOrderFile || null,
    additional_file: form?.additionalFile || null,
    additional_file_url: String(form?.additionalFileUrl || '').trim(),
    tax_preview: payloadPreview,
  };
};

export const buildExpenseStoreRequestData = (payload = {}) => {
  return buildExpenseFeeStoreRequestData(payload);
};
